<template>
  <!-- Plan 64: what would work for a waitlisted student (utils/waitlist.py). Used by the signup
       card's Waitlist action and the Waitlist view's Edit. Emits `save` with the parameters
       (api/endpoints/waitlist.py EntryBody); the caller sends them. -->
  <div class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" @click.self="$emit('close')">
    <div class="w-full max-w-lg bg-surface dark:bg-surface-dark rounded-xl shadow-xl max-h-[90vh] flex flex-col">
      <div class="flex items-center justify-between px-6 py-4 border-b border-stroke-subtle dark:border-stroke-dark flex-shrink-0">
        <h2 class="text-base font-semibold text-fg-base dark:text-fg-base-dark">{{ title }}</h2>
        <button @click="$emit('close')" class="text-fg-faint hover:text-fg-secondary dark:hover:text-fg-secondary-dark">
          <XMarkIcon class="h-5 w-5" />
        </button>
      </div>

      <div class="px-6 py-4 space-y-4 overflow-y-auto text-sm">
        <div class="grid grid-cols-2 gap-3">
          <label :class="labelClass">Student
            <input v-model="form.student_name" type="text" :class="inputClass" />
          </label>
          <label :class="labelClass">Instrument
            <input v-model="form.instrument" type="text" list="waitlist-instruments" :class="inputClass" />
            <datalist id="waitlist-instruments">
              <option v-for="i in instruments" :key="i" :value="i" />
            </datalist>
          </label>
        </div>

        <div>
          <p :class="labelClass">Days <span class="text-fg-faint">(none = any day)</span></p>
          <div class="flex flex-wrap gap-1.5 mt-1">
            <button v-for="(d, i) in DAYS" :key="d" type="button" @click="toggle(form.days, i)"
              :class="['px-2 py-1 rounded-md border text-xs', form.days.includes(i) ? chipOn : chipOff]">{{ d }}</button>
          </div>
        </div>

        <div>
          <p :class="labelClass">Times <span class="text-fg-faint">(none = any time)</span></p>
          <div v-for="(w, i) in form.time_windows" :key="i" class="flex items-center gap-2 mt-1">
            <input v-model="w.start" type="time" step="900" :class="inputClass" />
            <span class="text-fg-faint">to</span>
            <input v-model="w.end" type="time" step="900" :class="inputClass" />
            <button type="button" @click="form.time_windows.splice(i, 1)" class="text-fg-faint hover:text-red-600"><XMarkIcon class="h-4 w-4" /></button>
          </div>
          <button type="button" @click="form.time_windows.push({ start: '15:00', end: '19:00' })" class="mt-1 text-xs text-blue-600 dark:text-blue-400">+ Add a time range</button>
        </div>

        <label :class="labelClass">Lesson length
          <select v-model.number="form.duration_minutes" :class="inputClass">
            <option :value="30">30 minutes</option>
            <option :value="45">45 minutes</option>
            <option :value="60">60 minutes</option>
          </select>
        </label>

        <div>
          <label :class="labelClass">Teacher
            <select v-model="form.teacher_mode" :class="inputClass">
              <option value="any">Any teacher</option>
              <option value="prefer">Prefers certain teachers (others are fine)</option>
              <option value="only">Only certain teachers</option>
            </select>
          </label>
          <div v-if="form.teacher_mode !== 'any'" class="flex flex-wrap gap-1.5 mt-1.5">
            <button v-for="t in fitTeachers" :key="t.id" type="button" @click="toggle(form.teacher_ids, t.id)"
              :class="['px-2 py-1 rounded-md border text-xs', form.teacher_ids.includes(t.id) ? chipOn : chipOff]">{{ t.name }}</button>
          </div>
        </div>

        <div>
          <p :class="labelClass">Never offer <span class="text-fg-faint">(e.g. not the right fit)</span></p>
          <div class="flex flex-wrap gap-1.5 mt-1">
            <button v-for="t in fitTeachers" :key="t.id" type="button" @click="toggle(form.exclude_teacher_ids, t.id)"
              :class="['px-2 py-1 rounded-md border text-xs', form.exclude_teacher_ids.includes(t.id) ? chipNo : chipOff]">{{ t.name }}</button>
          </div>
        </div>

        <label :class="labelClass">Why they're waiting
          <select v-model="form.waiting_reason" :class="inputClass">
            <option v-for="(label, key) in reasons" :key="key" :value="key">{{ label }}</option>
          </select>
        </label>
        <p v-if="form.waiting_reason === 'other'" class="text-xs text-fg-muted dark:text-fg-muted-dark -mt-2">
          Never offered automatically. Use Invite now when a time fits.
        </p>

        <label :class="labelClass">Note
          <textarea v-model="form.note" rows="2" :class="inputClass" placeholder="e.g. asked for financial assistance" />
        </label>
        <p v-if="error" class="text-xs text-red-600 dark:text-red-400">{{ error }}</p>
      </div>

      <div class="flex justify-end gap-2 px-6 py-3 border-t border-stroke-subtle dark:border-stroke-dark flex-shrink-0">
        <button @click="$emit('close')" class="px-3 py-1.5 text-sm rounded-md border border-stroke-strong dark:border-stroke-strong-dark text-fg-secondary dark:text-fg-secondary-dark">Cancel</button>
        <button @click="save" :disabled="saving" class="px-3 py-1.5 text-sm rounded-md bg-blue-600 text-white disabled:opacity-50">{{ saving ? '…' : 'Save' }}</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, ref } from 'vue'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import { useApi } from '../../composables/useApi.js'

const props = defineProps({
  entry: { type: Object, default: () => ({}) },   // existing entry, or defaults from a signup
  title: { type: String, default: 'Waitlist' },
  teachers: { type: Array, default: null },       // from GET /api/waitlist; loaded here when absent
  reasons: { type: Object, default: () => ({ no_teacher: 'No teacher for this instrument', no_time: 'No time that works', other: 'Other (staff invite only)' }) },
  onSubmit: { type: Function, required: true },   // async (params) => void; throws to show an error
})
const emit = defineEmits(['close', 'saved'])
const { fetchApi } = useApi()

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const e = props.entry
const form = reactive({
  student_id: e.student_id ?? null,
  student_name: e.student_name ?? '',
  instrument: e.instrument ?? '',
  days: [...(e.days || [])],
  time_windows: (e.time_windows || []).map(w => ({ ...w })),
  duration_minutes: e.duration_minutes || 30,
  teacher_mode: e.teacher_mode || 'any',
  teacher_ids: [...(e.teacher_ids || [])],
  exclude_teacher_ids: [...(e.exclude_teacher_ids || [])],
  requested_time: e.requested_time ?? null,
  waiting_reason: e.waiting_reason || 'no_time',
  note: e.note ?? '',
})

const loaded = ref(props.teachers)
if (!loaded.value) fetchApi('/api/waitlist').then(r => { loaded.value = r.teachers }).catch(() => { loaded.value = [] })

const visible = computed(() => (loaded.value || []).filter(t => !t.hidden))
const instruments = computed(() => [...new Set(visible.value.flatMap(t => t.instruments))].sort())
// Teachers for this instrument, plus any already picked; everyone when no instrument is set.
const fitTeachers = computed(() => {
  const want = (form.instrument || '').toLowerCase().split(/\s*(?:,|\/|&|\bor\b|\band\b)\s*/).filter(Boolean)
  const picked = new Set([...form.teacher_ids, ...form.exclude_teacher_ids])
  return visible.value.filter(t => !want.length || picked.has(t.id) || t.instruments.some(i => want.includes(i.toLowerCase())))
})

const toggle = (list, v) => {
  const i = list.indexOf(v)
  if (i === -1) list.push(v)
  else list.splice(i, 1)
}

const saving = ref(false)
const error = ref('')
async function save() {
  saving.value = true
  error.value = ''
  try {
    await props.onSubmit({ ...form, time_windows: form.time_windows.filter(w => w.start && w.end && w.start < w.end) })
    emit('saved')
  } catch (err) {
    error.value = err.message || 'Could not save.'
  } finally {
    saving.value = false
  }
}

const labelClass = 'block text-xs text-fg-muted dark:text-fg-muted-dark'
const inputClass = 'block mt-0.5 w-full px-2.5 py-1.5 rounded-lg border border-stroke-strong dark:border-stroke-strong-dark bg-surface dark:bg-surface-muted-dark text-fg-base dark:text-fg-base-dark text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
const chipOff = 'border-stroke-strong dark:border-stroke-strong-dark text-fg-secondary dark:text-fg-secondary-dark'
const chipOn = 'border-blue-600 bg-blue-600 text-white'
const chipNo = 'border-red-600 bg-red-600 text-white'
</script>
